import React from "react";
import useConfig from "../../../../demos/hooks/useConfig";
import { useAIChat } from "../../hooks/useAIChat";
import { useAIChatV2 } from "../../hooks/useAIChatV2";
import { useAIChatV3 } from "../../hooks/useAIChatV3";
import { useAIChatV4 } from "../../hooks/useAIChatV4";
import { useAIChatV5 } from "../../hooks/useAIChatV5";

// Props interface for components that use AI Chat
interface AIChatComponentProps {
  // Define common props here if needed
  className?: string;
  children?: React.ReactNode;
}

// Higher-order component that provides the appropriate AI Chat hook
export function withAIChat<P extends object>(
  Component: React.ComponentType<P & { aiChat: any }>
) {
  return function WithAIChatComponent(props: P) {
    const aiChatV1 = useAIChat();
    const aiChatV2 = useAIChatV2();
    const aiChatV3 = useAIChatV3();
    const aiChatV4 = useAIChatV4();
    const aiChatV5 = useAIChatV5();
    const { config } = useConfig();

    let aiChat;
    if (config.useAIChatV5) {
      aiChat = aiChatV5;
    } else if (config.useAIChatV4) {
      aiChat = aiChatV4;
    } else if (config.useAIChatV3) {
      aiChat = aiChatV3;
    } else if (config.useAIChatV2) {
      aiChat = aiChatV2;
    } else {
      aiChat = aiChatV1;
    }

    return <Component {...props} aiChat={aiChat} />;
  };
}

// Hook selector that returns the appropriate AI Chat hook based on config
export function useAIChatSelector() {
  const { config } = useConfig();
  const aiChatV1 = useAIChat();
  const aiChatV2 = useAIChatV2();
  const aiChatV3 = useAIChatV3();
  const aiChatV4 = useAIChatV4();
  const aiChatV5 = useAIChatV5();

  if (config.useAIChatV5) {
    return aiChatV5 as any; // V5
  } else if (config.useAIChatV4) {
    return aiChatV4 as any; // Type assertion for V4 compatibility
  } else if (config.useAIChatV3) {
    return aiChatV3 as any; // Type assertion for V3 compatibility
  } else if (config.useAIChatV2) {
    return aiChatV2 as any; // Type assertion for V2 compatibility
  } else {
    return aiChatV1 as any; // Type assertion for V1 compatibility
  }
}

// Component for displaying which version is currently active
export function AIChatVersionIndicator({
  className = "",
}: AIChatComponentProps) {
  const { config } = useConfig();

  let version, description, badgeColor;

  if (config.useAIChatV5) {
    version = "v5";
    description = config.aiChatV5Provider === "cadviewer-analytics" ? "Powered By CADViewer" : "Snowflake Proxy";
    badgeColor = "bg-orange-100 text-orange-800 border-orange-200";
  } else if (config.useAIChatV4) {
    version = "v4";
    description = "Hybrid/semantic endpoint";
    badgeColor = "bg-purple-100 text-purple-800 border-purple-200";
  } else if (config.useAIChatV3) {
    version = "v3";
    description = "Direct Server Results AI";
    badgeColor = "bg-green-100 text-green-800 border-green-200";
  } else if (config.useAIChatV2) {
    version = "v2";
    description = "Search Engine Based AI";
    badgeColor = "bg-blue-100 text-blue-800 border-blue-200";
  } else {
    version = "v1";
    description = "Traditional AI Response";
    badgeColor = "bg-gray-100 text-gray-800 border-gray-200";
  }

  return (
    <div
      className={`ai-chat-version-indicator flex items-center space-x-2 ${className}`}
    >
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${badgeColor}`}
      >
        AI Chat {version}
      </span>
      <span className="text-gray-500 text-xs hidden sm:inline">
        {description}
      </span>
    </div>
  );
}

// Compact version for small spaces
export function AIChatVersionBadge({ className = "" }: AIChatComponentProps) {
  const { config } = useConfig();

  let version, badgeColor, title;

  if (config.useAIChatV5) {
    version = "v5";
    badgeColor = "bg-orange-500 text-white";
    title = config.aiChatV5Provider === "cadviewer-analytics" ? "AI Chat V5 - Powered By CADViewer" : "AI Chat V5 - Snowflake Proxy";
  } else if (config.useAIChatV4) {
    version = "v4";
    badgeColor = "bg-purple-500 text-white";
    title = "AI Chat V4 - Hybrid/Semantic Endpoint";
  } else if (config.useAIChatV3) {
    version = "v3";
    badgeColor = "bg-green-500 text-white";
    title = "AI Chat V3 - Direct Server Results";
  } else if (config.useAIChatV2) {
    version = "v2";
    badgeColor = "bg-blue-500 text-white";
    title = "AI Chat V2 - Search Engine Based";
  } else {
    version = "v1";
    badgeColor = "bg-gray-500 text-white";
    title = "AI Chat V1 - Traditional Response";
  }

  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${badgeColor} ${className}`}
      title={title}
    >
      {version}
    </span>
  );
}

export default {
  withAIChat,
  useAIChatSelector,
  AIChatVersionIndicator,
  AIChatVersionBadge,
};
